import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshIcon } from '@/components/icons/FilterIcons';
import { useTranslation } from '@/i18n';
import { useTheme } from '@/theme/useTheme';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
const UPDATE_TIMEOUT_MS = 15 * 1000;

/**
 * Shows a toast-style prompt when a new version of the app is available.
 * Uses registerType: 'prompt' so the user controls when to update.
 */
export function PwaUpdatePrompt() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateFailed, setUpdateFailed] = useState(false);
  const updateCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        if (updateCheckIntervalRef.current) {
          clearInterval(updateCheckIntervalRef.current);
        }

        updateCheckIntervalRef.current = setInterval(() => {
          void registration.update();
        }, UPDATE_CHECK_INTERVAL_MS);

        // Remove the obsolete /react/ registration after the new root-scoped
        // worker is registered. It could otherwise keep controlling asset URLs.
        if (new URL(registration.scope).pathname === '/') {
          void navigator.serviceWorker.getRegistrations().then((registrations) =>
            Promise.all(
              registrations
                .filter(
                  (candidate) =>
                    candidate !== registration &&
                    new URL(candidate.scope).pathname === '/react/'
                )
                .map((candidate) => candidate.unregister())
            )
          );
        }
      }
    },
  });

  useEffect(() => () => {
    if (updateCheckIntervalRef.current) clearInterval(updateCheckIntervalRef.current);
    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
  }, []);

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true);
    setUpdateFailed(false);

    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    updateTimeoutRef.current = setTimeout(() => {
      setIsUpdating(false);
      setUpdateFailed(true);
    }, UPDATE_TIMEOUT_MS);

    try {
      await updateServiceWorker(true);
      // A successful activation reloads the page. The timeout remains as a
      // recovery path if the browser cannot hand control to the new worker.
    } catch {
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
      setIsUpdating(false);
      setUpdateFailed(true);
    }
  }, [updateServiceWorker]);

  if (!needRefresh) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-busy={isUpdating}
      style={{
        position: 'fixed',
        bottom: theme.spacing.xl,
        right: theme.spacing.xl,
        left: theme.spacing.xl,
        zIndex: theme.zIndex.toast,
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: theme.spacing.md,
        width: 'fit-content',
        maxWidth: '440px',
        marginLeft: 'auto',
        padding: theme.spacing.lg,
        background: theme.colors.brandPrimary,
        color: theme.colors.textOnDark,
        borderRadius: theme.borderRadius.xxl,
        boxShadow: theme.shadows.modal,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSizeBase,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'grid',
          placeItems: 'center',
          width: '36px',
          height: '36px',
          flexShrink: 0,
          borderRadius: theme.borderRadius.full,
          backgroundColor: theme.colors.overlayWhiteMuted,
        }}
      >
        <RefreshIcon size={18} />
      </span>

      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            marginBottom: '2px',
            fontWeight: theme.typography.fontWeightSemibold,
            lineHeight: theme.typography.lineHeightTight,
          }}
        >
          {t('::PwaUpdate:Title')}
        </span>
        <span
          style={{
            display: 'block',
            color: updateFailed ? theme.colors.warningLight : theme.colors.textLight,
            fontSize: theme.typography.fontSizeSm,
            lineHeight: theme.typography.lineHeightNormal,
          }}
        >
          {updateFailed
            ? t('::PwaUpdate:Failed')
            : t('::PwaUpdate:Description')}
        </span>
      </span>

      <button
        type="button"
        onClick={handleUpdate}
        disabled={isUpdating}
        style={{
          background: theme.colors.brandAccent,
          color: theme.colors.textOnDark,
          border: 'none',
          borderRadius: theme.borderRadius.md,
          minHeight: '40px',
          padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
          fontWeight: theme.typography.fontWeightSemibold,
          fontSize: theme.typography.fontSizeSm,
          cursor: isUpdating ? 'wait' : 'pointer',
          opacity: isUpdating ? 0.75 : 1,
          whiteSpace: 'nowrap',
          transition: `opacity ${theme.transitions.default}, background-color ${theme.transitions.default}`,
        }}
      >
        {isUpdating
          ? t('::PwaUpdate:Updating')
          : updateFailed
            ? t('::PwaUpdate:Retry')
            : t('::PwaUpdate:Action')}
      </button>
    </div>
  );
}
